import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '@tenant/utility/constants/default';
import { checkAllowedAccess } from '@tenant/core/AppPermission/checkAllowedAccess.helper';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import { SYSTEM_ACTOR_USER_ID } from './constants/entityTypes';

// Pure, DOM/React-free helpers for the audit log view (developer-portal-audit-read-ui,
// Portion A). Extracted here — rather than inlined in components — specifically so they are
// unit-testable without a component/DOM harness (this repo has none).

/**
 * Maps FE filter state + paging into the exact query-string params AuditController's
 * [FromQuery] AuditQuery DTO binds (AuditQuery.cs) — PascalCase, matching the DTO's own
 * property names exactly. Every field is optional and omitted entirely when not set.
 * SystemActorOnly takes precedence over ActorUserId server-side
 * (AuditQueryService.BuildFilter) — only one is ever sent so the two controls can never
 * silently conflict on the wire.
 */
export const buildAuditQueryParams = (
  filterState = {},
  page = DEFAULT_PAGE_INDEX,
  pageSize = DEFAULT_PAGE_SIZE,
) => {
  const params = {
    Page: page,
    PageSize: pageSize,
  };

  if (filterState.entityType) params.EntityType = filterState.entityType;
  if (filterState.entityId) params.EntityId = filterState.entityId;

  if (filterState.systemActorOnly) {
    params.SystemActorOnly = true;
  } else if (filterState.actorUserId) {
    params.ActorUserId = filterState.actorUserId;
  }

  if (filterState.occurredFrom) params.OccurredFrom = filterState.occurredFrom;
  if (filterState.occurredTo) params.OccurredTo = filterState.occurredTo;
  if (filterState.correlationId) params.CorrelationId = filterState.correlationId;

  if (
    filterState.appId !== undefined &&
    filterState.appId !== null &&
    filterState.appId !== ''
  ) {
    params.AppId = filterState.appId;
  }

  return params;
};

/**
 * Maps raw AuditLogDocument-shaped Items (AuditQueryResult.Items) into view rows, deriving
 * the display flags the table/chip rendering needs. Null-safe: a null/undefined
 * ActorUserId or AppId must render without error (AC-16), never throw.
 */
export const mapAuditRowsFromResponse = (items = []) => {
  return (items ?? []).map((item) => {
    const isSystemActor = !item?.ActorUserId || item.ActorUserId === SYSTEM_ACTOR_USER_ID;
    const isPlatformGlobal = item?.AppId === null || item?.AppId === undefined;

    return {
      ...item,
      IsSystemActor: isSystemActor,
      IsPlatformGlobal: isPlatformGlobal,
      ActorLabel: isSystemActor ? 'System' : item?.ActorUserName || item?.ActorEmail || 'Unknown',
    };
  });
};

// Mirrors AuditQueryService.MaxDateRangeSpan (BE/MarketplaceDeveloper.WebAPI/Services/AuditQueryService.cs).
const MAX_DATE_RANGE_SPAN_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * Computes the {occurredFrom, occurredTo} window for "Load earlier records" (AC-13) — pages
 * the date window backward by the same span as the current window (or the server's default
 * 90-day span-cap when no explicit window was applied), anchored at the oldest record
 * currently shown so the next fetch doesn't re-request rows already on screen. No cursor —
 * mirrors the Wave 1 WindowClamped date-window-clamp model (AuditQueryService.ResolveDateRange).
 */
export const computeLoadEarlierWindow = (currentFrom, currentTo, oldestShownOccurredAt) => {
  const anchorMs = oldestShownOccurredAt
    ? new Date(oldestShownOccurredAt).getTime()
    : currentFrom
      ? new Date(currentFrom).getTime()
      : Date.now();

  const spanMs =
    currentFrom && currentTo
      ? new Date(currentTo).getTime() - new Date(currentFrom).getTime()
      : MAX_DATE_RANGE_SPAN_MS;

  const effectiveSpanMs = spanMs > 0 ? spanMs : MAX_DATE_RANGE_SPAN_MS;

  return {
    occurredFrom: new Date(anchorMs - effectiveSpanMs).toISOString(),
    occurredTo: new Date(anchorMs).toISOString(),
  };
};

/**
 * Portion A: the AppId filter is free/global for any internal AUDIT_READ holder. Kept as a
 * standalone predicate (rather than inlining a useAllowedAccess call) so Portion B can later
 * swap in an owner-scope check without restructuring the filter/table components, and so
 * it's unit-testable without a component harness.
 */
export const shouldShowAppIdFilter = (permissions = []) =>
  checkAllowedAccess(permissions, [AuthorizePermission.AUDIT_READ]);
